/**
 * Display formatting: friendly names for domain enums, dates, and quantities.
 * Pure functions only — safe to use anywhere.
 */

import type { DayRestriction, DripLayout, NozzleType, PlantType, Slope, SoilType, SunExposure, ZoneDriftField } from '@irrigation/core';

export const PLANT_LABELS: Record<PlantType, string> = {
  COOL_SEASON_GRASS: 'Cool-season grass',
  WARM_SEASON_GRASS: 'Warm-season grass',
  SHRUBS: 'Shrubs',
  TREES: 'Trees',
  XERISCAPE: 'Xeriscape',
  ANNUALS: 'Annual flowers',
  PERENNIALS: 'Perennial flowers',
  VEGETABLE_GARDEN: 'Vegetable garden',
};

export const SOIL_LABELS: Record<SoilType, string> = {
  SAND: 'Sand',
  LOAMY_SAND: 'Loamy sand',
  SANDY_LOAM: 'Sandy loam',
  LOAM: 'Loam',
  SILT_LOAM: 'Silt loam',
  CLAY_LOAM: 'Clay loam',
  CLAY: 'Clay',
};

export const SUN_LABELS: Record<SunExposure, string> = {
  FULL_SUN: 'Full sun',
  PARTIAL_SHADE: 'Partial shade',
  FULL_SHADE: 'Full shade',
};

export const SLOPE_LABELS: Record<Slope, string> = {
  FLAT: 'Flat',
  SLIGHT: 'Slight slope',
  MODERATE: 'Moderate slope',
  STEEP: 'Steep slope',
};

export const NOZZLE_LABELS: Record<NozzleType, string> = {
  FIXED_SPRAY: 'Fixed spray head',
  ROTOR: 'Rotor',
  ROTARY_NOZZLE: 'Rotary nozzle',
  DRIP: 'Drip',
  BUBBLER: 'Bubbler',
};

export const DRIP_LAYOUT_LABELS: Record<DripLayout, string> = {
  INLINE_DRIPLINE: 'Inline dripline (grid tubing)',
  POINT_DENSE: 'Point emitters — dense',
  POINT_SPARSE: 'Point emitters — sparse',
  MICRO_SPRAY: 'Micro-spray / bubbler',
};

/** Human names for the zone fields drift compares, for any screen reporting it. */
export const DRIFT_FIELD_LABELS: Record<ZoneDriftField['field'], string> = {
  plantType: 'Plant type',
  name: 'Name',
  nozzleType: 'Nozzle type',
  precipRateInPerHr: 'Application rate',
  efficiency: 'Efficiency',
  soilType: 'Soil type',
  slope: 'Slope',
  sunExposure: 'Sun exposure',
  rootDepthIn: 'Root depth',
  areaSqFt: 'Area',
};

export const SKIP_LABELS: Record<string, string> = {
  RAIN: 'rain',
  RAIN_AHEAD: 'rain expected',
  FREEZE: 'freeze',
  WIND: 'wind',
  DAY_RESTRICTION: 'day restriction',
  PAUSED: 'paused',
  WINDOW_FULL: 'window full',
  NEW_SEED: 'new seed misting',
};

/** "Every day", "Mon/Wed/Fri", "Every 3 days", … for a day restriction. */
export function describeDays(days: DayRestriction): string {
  switch (days.kind) {
    case 'ANY':
      return 'Every day';
    case 'ODD_DAYS':
      return 'Odd days';
    case 'EVEN_DAYS':
      return 'Even days';
    case 'SPECIFIC_DAYS':
      return days.weekdays.map((d) => WEEKDAY_LABELS[d] ?? '?').join('/');
    case 'INTERVAL':
      return `Every ${days.everyNDays} day${days.everyNDays > 1 ? 's' : ''}`;
  }
}

/** Gallons for a given depth over an area: 1 in over 1 sq ft ≈ 0.623 gal. */
export function gallons(depthIn: number, areaSqFt: number): number {
  return depthIn * areaSqFt * 0.623;
}

export const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** Tomorrow's ISO date, local time. */
export function tomorrowIso(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return toIsoDate(d);
}

/** ISO date `n` days before today, local time. */
export function recentDates(n: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    out.push(toIsoDate(d));
  }
  return out;
}

export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** "Today", "Yesterday", or "Monday, Jun 30" for an ISO date. */
export function friendlyDate(isoDate: string): string {
  const today = toIsoDate(new Date());
  if (isoDate === today) return 'Today';
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (isoDate === toIsoDate(yesterday)) return 'Yesterday';

  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

/** "Sep 19": a bare month and day, for a date inside the current season. */
export function shortDate(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** ISO date `days` after the given ISO date (local calendar math). */
export function isoDatePlus(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  const out = new Date(y, m - 1, d);
  out.setDate(out.getDate() + days);
  return toIsoDate(out);
}

/** "0.42 in" with sensible precision. */
/**
 * A sensor percentage, to one decimal and without a trailing ".0".
 *
 * Readings arrive from an external device and were interpolated raw, so a probe
 * reporting a float rendered every digit it had — "Soil 36.014934509803915%" —
 * which on a phone wrapped to three lines and was read out in full by a screen
 * reader.
 */
export function fmtPct(pct: number): string {
  return `${Math.round(pct * 10) / 10}%`;
}

export function fmtInches(inches: number): string {
  return `${inches.toFixed(inches >= 1 ? 1 : 2)} in`;
}

/** "1 h 05 min" / "42 min". */
export function fmtMinutes(minutes: number): string {
  const m = Math.round(minutes);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} min`;
}

/** "06:00" → "6:00 AM". Falls back to the input if unparsable. */
export function fmtTime(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  if (h === undefined || m === undefined || Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** Time-of-day of an ISO datetime — "9:14 PM". Empty when unparsable. */
export function clockTime(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  return new Date(ms).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

/** "just now", "12 min ago", "3 h ago", "2 d ago" for an ISO datetime. */
export function relativeTime(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return 'just now';
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}
